/**
 * Scenarios – guidance + impact summary
 *
 * QuestionPlaybook: concise, step-by-step "how to run this scenario" panel keyed
 * to the management question. Covers what to input, what the driver changes,
 * what to review (FY + Rolling 12M), and what to do next. Question keys come from
 * the real API templates.
 *
 * InvestmentSensitivity: a demo-friendly explainer for FY impact and Rolling
 * 12-month impact. When there is no driver or no computed impact, it says what
 * is missing instead of showing an unexplained dash.
 */

import {
  ArrowRight,
  ClipboardList,
  Eye,
  Info,
  ListChecks,
  SlidersHorizontal,
} from "lucide-react";
import { formatVariance } from "@/components/tekledger-ui";
import type { Scenario } from "@workspace/api-client-react";
import { QUESTION_PLAYBOOKS } from "./utils";

// ─── Question playbook ───────────────────────────────────────────────────────────

export function QuestionPlaybook({
  questionKey,
  question,
}: {
  questionKey: string;
  question?: string;
}) {
  const playbook = QUESTION_PLAYBOOKS[questionKey];
  if (!playbook) return null;

  const steps = [
    { icon: ClipboardList, label: "1 · What to input", body: playbook.input },
    {
      icon: SlidersHorizontal,
      label: "2 · What the driver changes",
      body: playbook.driverChange,
    },
    { icon: Eye, label: "3 · What to review", body: playbook.review },
    { icon: ArrowRight, label: "4 · What to do next", body: playbook.next },
  ];

  return (
    <div className="sc-playbook" data-testid={`question-playbook-${questionKey}`}>
      <div className="sc-playbook__head">
        <ListChecks size={13} />
        <div>
          <strong>How to run this scenario</strong>
          <p>{question ?? playbook.summary}</p>
        </div>
      </div>
      <ol className="sc-playbook__steps">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <li key={step.label} className="sc-playbook__step">
              <span className="sc-playbook__step-icon">
                <Icon size={13} />
              </span>
              <div>
                <span className="sc-playbook__step-label">{step.label}</span>
                <p>{step.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ─── Investment sensitivity / impact summary ─────────────────────────────────────

type ImpactState =
  | { kind: "no-driver" }
  | { kind: "not-computed" }
  | { kind: "computed" };

function deriveImpactState(scenario: Scenario): ImpactState {
  const hasDrivers = (scenario.drivers?.length ?? 0) > 0;
  if (!hasDrivers) return { kind: "no-driver" };
  const hasResults = (scenario.monthlyResults?.length ?? 0) > 0;
  const hasImpact =
    scenario.fyImpact !== 0 || scenario.rolling12Impact !== 0 || hasResults;
  if (!hasImpact) return { kind: "not-computed" };
  return { kind: "computed" };
}

function ImpactValue({
  value,
  label,
  sub,
  state,
  missingLine,
  testId,
}: {
  value: number;
  label: string;
  sub: string;
  state: ImpactState;
  missingLine: string;
  testId: string;
}) {
  const tone = value > 0 ? "bad-text" : value < 0 ? "good-text" : "";
  return (
    <div className="sc-sensitivity__metric" data-testid={testId}>
      <span className="sc-sensitivity__metric-label">{label}</span>
      {state.kind === "computed" ? (
        <>
          <strong className={tone}>{formatVariance(value)}</strong>
          <span className="sc-sensitivity__metric-sub">{sub}</span>
        </>
      ) : (
        <>
          <strong className="sc-sensitivity__pending">Not available yet</strong>
          <span className="sc-sensitivity__metric-sub">{missingLine}</span>
        </>
      )}
    </div>
  );
}

export function InvestmentSensitivity({ scenario }: { scenario: Scenario }) {
  const state = deriveImpactState(scenario);

  const missingLine =
    state.kind === "no-driver"
      ? "Add a driver to model the assumption."
      : "Recompute to project the impact.";

  return (
    <div className="sc-sensitivity" data-testid="investment-sensitivity">
      <div className="sc-sensitivity__head">
        <SlidersHorizontal size={13} />
        <div>
          <strong>Investment sensitivity</strong>
          <p>
            How much this scenario moves the plan versus the authoritative
            baseline. Unfavorable swings show as U; favorable savings show as F.
          </p>
        </div>
      </div>

      <div className="sc-sensitivity__grid">
        <ImpactValue
          value={scenario.fyImpact}
          label="FY impact"
          sub={`Full-year swing for FY${scenario.fiscalYear}`}
          state={state}
          missingLine={missingLine}
          testId="sensitivity-fy-impact"
        />
        <ImpactValue
          value={scenario.rolling12Impact}
          label="Rolling 12-month impact"
          sub="Forward-looking 12-month horizon"
          state={state}
          missingLine={missingLine}
          testId="sensitivity-rolling-impact"
        />
      </div>

      {state.kind !== "computed" && (
        <div className="sc-sensitivity__missing" data-testid="sensitivity-missing">
          <Info size={12} />
          <span>
            {state.kind === "no-driver" ? (
              <>
                <strong>No driver yet.</strong> Add at least one driver, then
                recompute so FY and Rolling 12-month impact can be calculated.
              </>
            ) : (
              <>
                <strong>Impact not computed.</strong> This scenario has drivers
                but no projected results — recompute to refresh FY and Rolling
                12-month impact.
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
